/* eslint-disable global-require, new-cap, import/no-dynamic-require */
import $ from 'jquery';

import GlobalEvents from 'app/GlobalEvents';
import RegisterRouter from 'app/router/RegisterRouter';
import BaseController from 'app/src/controllers/base/BaseController';
import PaymentController from 'app/src/controllers/payments/PaymentController';
import Decimal from 'app/src/data/base/Decimal';
import CommonData from 'app/src/data/CommonData';
import { Eventbus } from 'app/src/data/Eventbus';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import PaymentType from 'app/src/data/models/PaymentType';
import SalePaymentCollection from 'app/src/data/models/SalePaymentCollection';
import UITooltip from 'app/src/data/models/UITooltip';
import UITooltipCollection from 'app/src/data/models/UITooltipCollection';
import Filter from 'app/src/data/models/utils/Filter';
import Localize from 'app/src/localize/Localize';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import SaleManager from 'app/src/payments/SaleManager';
import SocketClient from 'pages/views/devices/socketClient';
import { CFD_MESSAGES } from 'pages/views/devices/socketClient/types';
import { showCancelSaleDialog } from 'payments/components/CancelSaleDialog';
import { setQuickPayState, doCreditQuickPay, doCashQuickPay } from 'payments/QuickPay';
import TerminalPayment from 'payments/TerminalPayment';
import { selectTerminal } from 'root/apps/payments/components/TerminalSelectionModal';
import { TipModalWrapper } from 'root/apps/payments/TippingModal';
import Modules from 'shared_modules/Modules';
import Monitoring from 'shared_modules/Monitoring';

export default class RegisterController extends BaseController {
  constructor(options = {}) {
    super(options);
    this._router = new RegisterRouter({ controller: this });
    this._paymentController = null;
    this._saleManager = null;
    this._finishSaleInProgress = null;
    // we don't control the register view (still done using PHP) so just listen
    // for events on the global channel
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.PAYMENTS_START,
      this.onStartPayments
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.PAYMENTS_CLOSE,
      this.onClosePayments
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.SALE_UPDATING,
      this.onSaleUpdating
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.SALE_UPDATED,
      this.onSaleUpdated
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.SHOW_PAYMENTS,
      this.onShowPayments
    );

    if (Modules.hasModule('gui.payment.quickpay')) {
      this.listenTo(
        this._globalChannel.vent,
        GlobalEvents.Events.register.QUICK_CARD_PAY_REQUESTED,
        this.onQuickPayCardRequested
      );
      this.listenTo(
        this._globalChannel.vent,
        GlobalEvents.Events.register.QUICK_CASH_PAY_REQUESTED,
        this.onQuickPayCashRequested
      );
    }

    this._globalChannel.reqres.setHandler(
      GlobalEvents.Events.register.SHIPPING_UPDATED,
      this.shippingUpdated.bind(this)
    );
    this._globalChannel.reqres.setHandler(
      GlobalEvents.Events.register.PAYMENTS_FINISH,
      this.finishPayments.bind(this)
    );
    this._globalChannel.reqres.setHandler(
      GlobalEvents.Events.register.CONFIRM_FOR_UNSAVED_CHANGES,
      this.confirmUnsavedChanges.bind(this)
    );
    this._globalChannel.reqres.setHandler(
      GlobalEvents.Events.register.DEPOSIT_REFUNDS_REQUESTED,
      this.depositRefundsRequested.bind(this)
    );

    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.CANCEL_SALE_REQUESTED,
      this.onCancelSaleRequested
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.REGISTER_TIP_REASSIGN,
      this.onTipReassignment
    );
  }

  /**
   * Gui Defs does this really smart thing where it just completely replaces
   * HTML on the page. When that happens, we never re-load the register.
   * Everything is already initilized so just display the right screen
   */
  onShowPayments() {
    this._showPayments();
  }

  /**
   * Event handler for when a register URL is accessed.  It will instantiate a SaleManager
   * instance and, optionally, show the payments block (if necessary).
   *
   * @param {string} formName The value of the form_name URL parameter (if present)
   */
  onRoute(formName) {
    let $register = $('#register');
    let $transactionID = $('#popdisplay_transactionid');
    let transactionID = $transactionID.length
      ? $transactionID.val()
      : $register.attr('transaction_id');

    this._showNewFeatures();

    if (transactionID) {
      // store the promise temporarily so we can synchronize to when the sale manager is
      // finished initialization
      window.showNotifier('working');
      this._saleManagerInitialize = $.when(
        SaleManager.continueSale(transactionID),
        PaymentProvider.getCurrentShopPaymentProvider()
      )
        .done((manager, paymentProvider) => {
          window.hideNotifier('working');
          this._saleManager = manager;
          this._paymentProvider = paymentProvider;

          const { pendingCreditDebitTransaction } = this._saleManager;
          if (
            pendingCreditDebitTransaction &&
            this._paymentProvider.activeGateway.get('gatewayID') ===
              GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
          ) {
            TerminalPayment.doTerminalPayment({
              initialAmount: pendingCreditDebitTransaction.get('totalAmount'),
              saleManager: this._saleManager,
              paymentProvider: this._paymentProvider,
              recoveredPayment: pendingCreditDebitTransaction,
            });
          }

          // Normally, we'll show the payments if we detect 'do_payments'
          // in the URL but there are times (for example when continuing a sale)
          // where it is necessary to look in the DOM to see what's currently
          // there to determine if we need to show payments.
          if (
            formName === 'do_payments' ||
            $('div.do_payments').length ||
            $('div[data-action="do_payments"]').length
          ) {
            this._showPayments();
          }

          this._saleManagerInitialize = null;

          if (
            Modules.hasModule('gui.payment.quickpay') &&
            this._paymentProvider &&
            this._paymentProvider.activeGateway &&
            (this._paymentProvider.activeGateway.get('gatewayID') ===
              GatewayConfig.GatewayType.VANTIV_TRIPOS ||
              this._paymentProvider.activeGateway.get('gatewayID') ===
                GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS)
          ) {
            setQuickPayState(this._saleManager, this._paymentProvider);
          }
        })
        .fail(errorMessage => {
          window.alertUser(Localize.translate(errorMessage), 300);
          window.merchantos.register.continueSale();
        });
    }
  }

  /**
   * Event handler for when the 'payments:start' event is triggered.
   */
  onStartPayments() {
    if (!this._saleManagerInitialize && !this._saleManager) {
      window.location.reload();
    }

    // It's possible for closePayments() to be called before the
    // sale manager is initialized. This forces the controller
    // to clean itself up if need be.
    this._closePayments();

    // we need to wait for the sale manager to initialize first
    if (!this._saleManager) {
      this._saleManagerInitialize.done(() => {
        this._showPayments();
      });
    } else {
      this._showPayments();
    }
  }

  /**
   * Event handler for when the 'payments:close' event is triggered.
   */
  onClosePayments() {
    /*
    We are allowing Payment Close to call _closePayment even on new UI.
    This is because _closePayment does more than just close payment, it sets the state for quick pay
     */
    if (Modules.hasModule('gui.payment.revamp')) {
      Eventbus.emit(GlobalEvents.Events.register.PAYMENTS_CLOSE);
    }
    this._closePayments();
  }

  onSaleUpdating() {
    if (this._saleManager) {
      this._saleManager.isSaleUpdating = true;
    }
  }

  /**
   * Event handler for when the 'payments:sale:updated' event is triggered.
   */
  onSaleUpdated() {
    let $register = $('#register');
    let $transactionID = $('#popdisplay_transactionid');
    let transactionID = $transactionID.length
      ? $transactionID.val()
      : $register.attr('transaction_id');

    // In some edge cases after few navigation search the saleManager becomes undefined
    // we just make sure to re-initialized if this happen
    if (!this._saleManager) {
      this._saleManagerInitialize = $.when(SaleManager.continueSale(transactionID)).done(
        manager => {
          this._saleManager = manager;
          if (
            Modules.hasModule('gui.payment.quickpay') &&
            this._paymentProvider &&
            this._paymentProvider.activeGateway &&
            (this._paymentProvider.activeGateway.get('gatewayID') ===
              GatewayConfig.GatewayType.VANTIV_TRIPOS ||
              this._paymentProvider.activeGateway.get('gatewayID') ===
                GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS)
          ) {
            setQuickPayState(this._saleManager, this._paymentProvider);
          }
          this._saleManager.isSaleUpdating = false;
        }
      );
    } else {
      this._saleManager.refreshSale().then(() => {
        if (
          Modules.hasModule('gui.payment.quickpay') &&
          this._paymentProvider &&
          this._paymentProvider.activeGateway &&
          (this._paymentProvider.activeGateway.get('gatewayID') ===
            GatewayConfig.GatewayType.VANTIV_TRIPOS ||
            this._paymentProvider.activeGateway.get('gatewayID') ===
              GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS)
        ) {
          setQuickPayState(this._saleManager, this._paymentProvider);
        }
        this._saleManager.isSaleUpdating = false;
      });
    }
  }

  /**
   * Event handler for when the 'payments:shipping:updated' event is triggered
   */
  shippingUpdated() {
    return this._saleManager ? this._saleManager.refreshSale() : $.Deferred().resolve();
  }

  /**
   * Request handler for when a 'payments:finish' request is made.
   *
   * @returns {jQuery.Promise|*}
   */
  finishPayments() {
    let promise = this._finishSaleInProgress;
    if (!promise) {
      this._finishSaleInProgress = promise = this._saleManager.finishSale();
    }

    promise
      .fail(result => {
        if (result) {
          if (result.error === SaleManager.Errors.USER_FACING) {
            // TODO: replace this mechanism to remove the dependency on the legacy implementation
            alertUser(result.msg);
          } else {
            // TODO: replace this mechanism to remove the dependency on the legacy implementation
            let alertText =
              `${Localize.translate(
                `The sale failed to complete. ${result.msg}<br>`
              )}<a href="/register.php" class="strong-link">` +
              'Click-here to recover payment information.</a>';
            alertUser(alertText);
          }
        }
      })
      .always(() => {
        this._finishSaleInProgress = null;
      });

    return promise;
  }

  /**
   * Method which is invoked when the user has elected to navigate away from the register which
   * prompts for confirmation with a message if there are unsaved credit card payments in the
   * sale.
   *
   * @returns {boolean}   True if the user has selected to proceed
   */
  confirmUnsavedChanges() {
    let confirmed = true;

    if (this._saleManager) {
      if (this._saleManager.hasUnsavedCreditPayments()) {
        confirmed = confirm(
          Localize.translate(
            'The current sale has a credit card payment attached that has not ' +
              'been saved.\n  Do you wish to continue?'
          )
        );
      }
    }

    if (confirmed) {
      // We need to cleanup our data so that if this gets called again we're not
      // referencing stale data.
      this._saleManager = null;

      // TODO Since we know that this method will be invoked when we're navigating away
      // from the payments, we can cleanup here.  We should look into managing cleanup
      // in general better since our settings and register controllers and views won't
      // cleanup currently simply by navigating away (which is an oversight).
    }

    return confirmed;
  }

  /**
   * Handler for when the deposit refunds requested handler is invoked.  This method
   * returns the deposit refunds that are currently in the sale.  It is designed
   * to return the data in a format consistent with how the legacy implementation
   * stores deposit refunds to be compatible with legacy methods.
   *
   * @return {Object.<number, number>}    An object hash with keys representing the
   *                                      payment type ID of the deposit and the value
   *                                      be the amount being refunded for that payment
   *                                      type
   */
  depositRefundsRequested() {
    let depositsInSale = {};

    if (this._saleManager) {
      const deposits = this._saleManager.deposits;
      deposits.depositRefunds.forEach(depositRefund => {
        const paymentTypeID =
          depositRefund.depositPayment.model.get('refPaymentID') !== 0
            ? `credit_card.${depositRefund.depositPayment.model.get('refPaymentID')}`
            : depositRefund.depositPayment.paymentTypeID;

        if (depositsInSale[paymentTypeID]) {
          depositsInSale[paymentTypeID] = new Decimal(depositsInSale[paymentTypeID])
            .plus(new Decimal(depositRefund.depositPayment.amount.abs()))
            .toString();
        } else {
          depositsInSale[paymentTypeID] = depositRefund.depositPayment.amount.abs().toString();
        }
      });

      if (!deposits.getInSaleDepositRefundAmount().isZero()) {
        // the only deposit refunds that can currently be in a sale are credit card deposits so
        // go find them

        let salePayments =
          this._saleManager.sale.getRelation('SalePayments', SalePaymentCollection) ||
          new SalePaymentCollection();

        salePayments.forEach(salePayment => {
          let paymentTypeRelation = salePayment.getRelation('PaymentType', PaymentType);

          if (
            paymentTypeRelation &&
            paymentTypeRelation.get('type') === PaymentType.Types.CREDIT_CARD &&
            !salePayment.get('archived') &&
            salePayment.get('amount').isNegative() &&
            salePayment.get('refPaymentID') !== 0
          ) {
            depositsInSale[`credit_card.${salePayment.get('refPaymentID')}`] = salePayment
              .get('amount')
              .abs()
              .toString();
          }
        });
      }
    }

    return depositsInSale;
  }

  /**
   * Event handler for when a quick pay credit transaction is requested
   */
  onQuickPayCardRequested() {
    if (this._saleManager) {
      if (this._saleManager.isTipEnabled) {
        const transactionAmount = this._saleManager.pendingBalance;
        selectTerminal({
          paymentProvider: this._paymentProvider,
          amount: transactionAmount,
        })
          .then(selectedTerminal => {
            Monitoring.NREvent('tip.start', 'RegisterController', {
              description: 'Merchant started the tip flow.',
              transactionAmount: transactionAmount?.toNumber() || null,
              quickPay: true,
            });
            this._startTipFlow({ transactionAmount })
              .then(({ tipAmount, totalAmountWithTip }) => {
                Monitoring.NREvent('tip.complete', 'RegisterController', {
                  description: 'Merchant completed the tip flow.',
                  transactionAmount: transactionAmount?.toNumber() || null,
                  tipAmount: tipAmount?.toNumber() || null,
                  totalAmountWithTip: totalAmountWithTip?.toNumber() || null,
                  quickPay: true,
                });

                doCreditQuickPay({
                  saleManager: this._saleManager,
                  paymentProvider: this._paymentProvider,
                  amount: totalAmountWithTip,
                  tipAmount,
                  terminal: selectedTerminal,
                });
              })
              .catch(() => {
                Monitoring.NREvent('tip.cancel', 'RegisterController', {
                  description: 'Merchant cancelled the tip flow.',
                  transactionAmount: transactionAmount?.toNumber() || null,
                  quickPay: true,
                });
              });
          })
          .catch(() => {});
      } else {
        doCreditQuickPay({
          saleManager: this._saleManager,
          paymentProvider: this._paymentProvider,
        });
      }
    }
  }

  _startTipFlow({ transactionAmount }) {
    const terminal = this._paymentProvider.getTerminal(
      CommonData.selectedRegister.get('ccTerminalID')
    );
    return TipModalWrapper.startTipFlow({
      terminal,
      saleManager: this._saleManager,
      transactionAmount,
    });
  }

  /**
   * Event handler for when a quick pay cash transaction is requested
   */
  onQuickPayCashRequested() {
    if (this._saleManager) {
      doCashQuickPay(this._saleManager);
    }
  }

  /**
   * Event handler for when the user request to cancel the sale
   */
  onCancelSaleRequested() {
    if (this._saleManager) {
      if (
        !this._saleManager.totalCreditPayments.isZero() &&
        PaymentProvider.supportedTerminalsForGateway(this._paymentProvider.activeGateway)
      ) {
        showCancelSaleDialog(() => {
          window.merchantos.utils.openUrl('/register.php?form_name=do_payments');
        });
      } else {
        if (confirm('Cancel the current sale?')) {
          SocketClient.send(CFD_MESSAGES.HIDE_SALE);
          window.merchantos.utils.openUrl('/register.php?form_name=cancel_transaction');
        }
      }
    }
  }

  /**
   * Event handler for when a tip reassignment is requested
   */
  onTipReassignment() {
    if (this._saleManager) {
      return TipModalWrapper.startTipReAssignFlow({ saleManager: this._saleManager });
    }
  }

  /**
   *
   * Shows the payments view.
   *
   * @private
   */
  _showPayments() {
    if (Modules.hasModule('gui.payment.revamp')) {
      Eventbus.emit(GlobalEvents.Events.register.SHOW_PAYMENTS, {
        data: { saleManager: this._saleManager, paymentProvider: this._paymentProvider },
      });
    } else {
      if (!this._paymentController) {
        this._paymentController = new PaymentController(this._saleManager, this._paymentProvider);
      }

      this._paymentController.enableLegacyControls();
      this._paymentController.showPayments();
    }
  }

  /**
   * Closes the payments view.
   *
   * @private
   */
  _closePayments() {
    if (this._paymentController) {
      this._paymentController.destroy();
      this._paymentController = null;
    }

    if (
      Modules.hasModule('gui.payment.quickpay') &&
      this._paymentProvider &&
      this._paymentProvider.activeGateway &&
      (this._paymentProvider.activeGateway.get('gatewayID') ===
        GatewayConfig.GatewayType.VANTIV_TRIPOS ||
        this._paymentProvider.activeGateway.get('gatewayID') ===
          GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS)
    ) {
      setQuickPayState(this._saleManager, this._paymentProvider);
    }
  }

  /**
   * Shows new features UI
   */
  _showNewFeatures() {
    const DISCOUNT_AND_TAX_FEATURE = 'discount_and_tax';

    let uiTooltipsFilter = new Filter().exactly('component', 'view.sales');
    let tooltips = new UITooltipCollection();

    tooltips.fetch(uiTooltipsFilter).done(() => {
      if (tooltips.length) {
        if (tooltips.find(tooltip => tooltip.get('name') === DISCOUNT_AND_TAX_FEATURE)) {
          // new discount and tax feature detected so show the appropriate UI
          $(`[feature=${DISCOUNT_AND_TAX_FEATURE}]`).removeClass('hidden');

          // POST the feature to prevent it from showing again
          new UITooltip({
            component: 'view.sales',
            name: DISCOUNT_AND_TAX_FEATURE,
          }).save();
        }
      }
    });
  }
}
